/**
 * Policy list (spec §8.4): persistent filter bar, urgency-tinted rows, status badges,
 * quick actions, bulk selection, and a footer carrying the filtered book value.
 */

import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCircle2, Download, MoreHorizontal, Plus, RefreshCw, Wallet } from 'lucide-react';
import { isPresumedNotRenewing, isRenewalDue, urgencyOf, type PolicyDto } from '@ias/shared';

import { Badge, type BadgeTone } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip } from '@/components/ui/misc';
import { DataTable, type DataColumn } from '@/components/data-table/data-table';
import { EntityCell } from '@/components/common/entity-avatar';
import { EnumBadge } from '@/components/common/enum-badge';
import { PageHeader } from '@/components/common/page-header';
import { PolicyRef } from '@/components/common/policy-ref';
import { FilterBar, type FilterDescriptor } from '@/components/filters/filter-bar';
import { download } from '@/lib/api/client';
import { policiesApi, useBulkPolicyAction, useInsuranceCompanies } from '@/lib/api/domains';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';
import { useListState } from '@/hooks/use-list-state';
import { cn } from '@/lib/utils';

import { MarkPaidDialog } from './mark-paid-dialog';
import { PolicyFormDialog } from './policy-form';
import { randomUUID } from '@/lib/uuid';

/**
 * Tone for the status badge — the one place «ληγμένο» decides whether it is still news.
 *
 * `expired` is red because a lapse is an unanswered renewal. Six months on it is not: the
 * archive import filed 4.549 policies whose last period closed years ago, and painting those
 * red made red the colour of the list rather than the colour of work. Past the horizon they
 * drop to the same neutral as `cancelled` — closed business, nothing to do. Returning
 * `undefined` leaves the registry's own tone in charge, so this only ever speaks up for the
 * case it owns.
 */
/**
 * Whether an expired policy is still a call to make — the one rule behind both its red row and
 * its red badge. Not when it has already been renewed as a separate policy (the archive import's
 * shape: a red «Ληγμένο» above its own renewal is what got ZTE8239 renewed twice), not when the
 * client said «Δεν ανανεώνει», and not past the six-month horizon, where it is history.
 */
function isLapseToChase(policy: PolicyDto): boolean {
  return (
    !policy.successorPolicyNumber &&
    policy.noticeStatus !== 'declined' &&
    !isPresumedNotRenewing(policy.endDate)
  );
}

export function policyStatusTone(policy: PolicyDto): BadgeTone | undefined {
  if (policy.status !== 'expired') return undefined;
  return isLapseToChase(policy) ? undefined : 'neutral';
}

/** Spec §8.4 row colouring, expressed once and reused by the renewals board. */
export function policyRowTint(policy: PolicyDto): string | undefined {
  if (policy.status === 'cancelled') return undefined;
  // Same rule as the badge: a lapse worth chasing is tinted; renewed, declined or dead is plain.
  if (policy.status === 'expired') {
    return isLapseToChase(policy) ? 'row-tint-critical' : undefined;
  }
  if (policy.paymentStatus === 'paid' && policy.status === 'active') return 'row-tint-good';

  const urgency = urgencyOf(policy.endDate);
  if (urgency === 'critical') return 'row-tint-critical';
  if (urgency === 'warning') return 'row-tint-serious';
  if (urgency === 'notice') return 'row-tint-warning';
  return undefined;
}

/**
 * Days-to-expiry as a badge — the single place that wording is decided.
 *
 * «0» was rendered bare, which is the one value a countdown cannot say as a number: zero
 * days is not a quantity an agent reads, it is *today*, and «Λήξη 13/08/2026 · 0» left them
 * working out which. Zero now names itself.
 *
 * The renewals board used to format the same figure with its own inline badge — same
 * expired case, but «0 ημ.» where this said «0». Two spellings of one fact is how «Σήμερα»
 * ends up appearing on one screen and not the next, so the board calls this instead and
 * passes what it genuinely owns: its band's tone, and the unit its narrow cards need.
 */
export function DaysBadge({
  days,
  /** Callers with their own urgency scale — the renewals board, the day list — pass it in. */
  tone: toneOverride,
  /** Appends «ημ.» — for contexts where the badge stands alone with no «Λήξη» label. */
  withUnit,
  className,
}: {
  days: number | undefined;
  tone?: BadgeTone;
  withUnit?: boolean;
  className?: string;
}) {
  const { t } = useI18n();
  if (days === undefined) return <span className="text-muted-foreground">—</span>;

  const tone =
    toneOverride ??
    (days < 0 ? 'neutral' : days < 7 ? 'critical' : days < 15 ? 'serious' : days < 30 ? 'warning' : 'outline');

  const label =
    days < 0
      ? t('common.expired')
      : days === 0
        ? t('common.today')
        : withUnit
          ? `${days} ${t('common.daysShort')}`
          : String(days);

  return (
    <Badge tone={tone} size="sm" className={cn('tabular', className)}>
      {label}
    </Badge>
  );
}

export function PolicyListPage() {
  const { t, tn, money, date } = useI18n();
  const navigate = useNavigate();
  const { can } = useAuth();

  /*
   * Newest entry first, not soonest expiry.
   *
   * `endDate asc` was the triage order, and it was right when the book was the office's own
   * data entry. The archive import changed the premise: 4.894 policies came in, the oldest
   * ending in 2010, so page one opened on a decade of dead paper and the work of the week was
   * nowhere near it. What the office reaches for is what was just entered.
   *
   * The expiry view is not lost — it is one click on the «Λήξη» header, and the Renewals board
   * and the call sheet are both still ordered by urgency, which is where triage belongs.
   */
  const list = useListState({ defaultSortBy: 'createdAt', defaultSortOrder: 'desc' });
  /*
   * D5 review outcome: lapsed business stays on top of page one so nothing slips — but a
   * lapsed policy recorded as «Δεν ανανεώνει» is resolved work, so by default it leaves the
   * list. The switch below brings those rows back; nothing is ever hidden from an explicit
   * filter, only from the untouched triage view.
   */
  const { data, isLoading, isFetching, error, refetch } = policiesApi.useList({
    ...list.params,
    ...(list.filters.showNonRenewing ? {} : { hideNonRenewing: 'true' }),
  });
  const { data: insurers } = useInsuranceCompanies();

  const [selected, setSelected] = React.useState<string[]>([]);
  const [payingPolicy, setPayingPolicy] = React.useState<PolicyDto | null>(null);
  const [formOpen, setFormOpen] = React.useState(false);

  const bulkAction = useBulkPolicyAction();

  const totals = (data?.meta as { totals?: { premium: string; commission: string } } | undefined)?.totals;

  const columns = React.useMemo<DataColumn<PolicyDto>[]>(
    () => [
      {
        id: 'policyNumber',
        // «Συμβόλαιο», not «Αρ. συμβολαίου»: the cell leads with the plate now, and a column
        // headed by one of the two things in it misdescribes the other. The sort follows the
        // cell: plate first, then the number — see Sortable in PolicyService.
        header: t('policy.one'),
        meta: { sortKey: 'policyNumber', headerLabel: t('policy.one') },
        cell: ({ row }) => (
          <div className="flex items-center gap-1.5">
            <PolicyRef policyNumber={row.original.policyNumber} plates={row.original.plates} />
            {row.original.daysUntilExpiry !== undefined &&
              row.original.daysUntilExpiry >= 0 &&
              row.original.daysUntilExpiry < 30 && (
                <Tooltip content={t('dashboard.upcomingRenewals')}>
                  <span className="bg-serious size-1.5 shrink-0 rounded-full" />
                </Tooltip>
              )}
          </div>
        ),
      },
      {
        id: 'clientName',
        header: t('client.one'),
        meta: { sortKey: 'clientName', headerLabel: t('client.one') },
        cell: ({ row }) => <span className="truncate font-medium">{row.original.clientName}</span>,
      },
      {
        id: 'insuranceCompanyName',
        header: t('policy.company'),
        meta: { sortKey: 'insuranceCompanyName', hideBelow: 'lg', headerLabel: t('policy.company') },
        cell: ({ row }) => <EntityCell name={row.original.insuranceCompanyName} muted />,
      },
      {
        id: 'insuranceType',
        header: t('policy.insuranceType'),
        meta: { sortKey: 'insuranceType', hideBelow: 'xl', headerLabel: t('policy.insuranceType') },
        cell: ({ row }) => <EnumBadge enum="insuranceType" value={row.original.insuranceType} size="sm" />,
      },
      {
        /*
         * Start beside end, because a period is read as a span. Hidden below lg: on a narrow
         * screen the end date is the one that drives every decision, and the start is context.
         */
        id: 'startDate',
        header: t('policy.startDate'),
        meta: { sortKey: 'startDate', numeric: true, hideBelow: 'lg', headerLabel: t('policy.startDate') },
        cell: ({ row }) => (
          <span className="text-muted-foreground whitespace-nowrap">{date(row.original.startDate)}</span>
        ),
      },
      {
        id: 'endDate',
        header: t('policy.endDate'),
        meta: { sortKey: 'endDate', numeric: true, headerLabel: t('policy.endDate') },
        cell: ({ row }) => <span className="whitespace-nowrap">{date(row.original.endDate)}</span>,
      },
      {
        id: 'daysUntilExpiry',
        header: t('policy.daysUntilExpiry'),
        meta: { sortKey: 'daysUntilExpiry', align: 'center', hideBelow: 'md', headerLabel: t('policy.daysUntilExpiry') },
        cell: ({ row }) => <DaysBadge days={row.original.daysUntilExpiry} />,
      },
      {
        id: 'premiumAmount',
        header: t('policy.premium'),
        meta: { sortKey: 'premiumAmount', align: 'right', numeric: true, headerLabel: t('policy.premium') },
        cell: ({ row }) => money(row.original.premiumAmount),
      },
      /*
         Καθαρά next to ολικά, hidden first on a narrow screen. Two money columns side by side is
         a lot, but they are the pair the office reads off the carrier's table and the commission
         column is unreadable without the base it was calculated on — a 17% of a gross figure that
         does not divide into it invites exactly the arithmetic that started this.

         A dash, not 0,00 €, when it is missing: the value is unknown rather than zero, and the
         column beside it already shows what an unknown base does to the commission.
      */
      {
        id: 'netPremiumAmount',
        header: t('policy.netPremium'),
        meta: {
          sortKey: 'netPremiumAmount',
          align: 'right',
          numeric: true,
          hideBelow: 'xl',
          headerLabel: t('policy.netPremium'),
        },
        cell: ({ row }) =>
          row.original.netPremiumAmount ? (
            money(row.original.netPremiumAmount)
          ) : (
            <span className="text-muted-soft">—</span>
          ),
      },
      {
        id: 'commissionAmount',
        header: t('policy.commission'),
        meta: {
          sortKey: 'commissionAmount',
          align: 'right',
          numeric: true,
          hideBelow: 'lg',
          headerLabel: t('policy.commission'),
        },
        cell: ({ row }) => (
          <span
            className={row.original.netPremiumAmount ? 'text-muted-foreground' : 'text-serious'}
            title={row.original.netPremiumAmount ? undefined : t('policy.netPremiumMissingHint')}
          >
            {money(row.original.commissionAmount)}
          </span>
        ),
      },
      {
        id: 'status',
        header: t('policy.status'),
        meta: { sortKey: 'status', headerLabel: t('policy.status') },
        cell: ({ row }) => (
          <EnumBadge
            enum="policyStatus"
            value={row.original.status}
            tone={policyStatusTone(row.original)}
            size="sm"
          />
        ),
      },
      {
        id: 'paymentStatus',
        header: t('policy.paymentStatus'),
        meta: { sortKey: 'paymentStatus', hideBelow: 'md', headerLabel: t('policy.paymentStatus') },
        cell: ({ row }) => (
          <EnumBadge enum="paymentStatus" value={row.original.paymentStatus} size="sm" variant="dot" />
        ),
      },
      {
        id: 'actions',
        header: '',
        meta: { width: '5.5rem', align: 'right' },
        cell: ({ row }) => {
          const policy = row.original;
          /*
           * `isRenewalDue`, not «not yet paid». Every renewal closes with payment_status
           * 'paid', so the old test hid the action on exactly the policies that need it —
           * 52 of them are sitting in renewal_due right now. The label follows the same rule
           * as the detail page: collect when something is owed, renew when nothing is.
           */
          const canRenew = can('policy:renew') && isRenewalDue(policy);
          const nothingToCollect = policy.paymentStatus === 'paid';
          const renewLabel = t(nothingToCollect ? 'action.renewPeriod' : 'action.markPaid');

          return (
            <div
              className="row-actions flex items-center justify-end gap-0.5"
              onClick={(event) => event.stopPropagation()}
            >
              {canRenew && (
                <Tooltip content={renewLabel}>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => setPayingPolicy(policy)}
                    aria-label={renewLabel}
                  >
                    {nothingToCollect ? (
                      <RefreshCw className="text-primary" />
                    ) : (
                      <CheckCircle2 className="text-good" />
                    )}
                  </Button>
                </Tooltip>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon-sm" aria-label={t('action.more')}>
                    <MoreHorizontal />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={() => navigate(`/policies/${policy.id}`)}>
                    {t('action.view')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => navigate(`/clients/${policy.clientId}`)}>
                    {t('client.one')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
      },
    ],
    [t, money, date, can, navigate],
  );

  /**
   * One idempotency key per selection.
   *
   * The unit of intent for a bulk action is "these policies, this action" — so the key
   * is regenerated whenever the selection changes, and stays fixed while it does not.
   * Two clicks on the same selection therefore collapse into one on the server, while
   * selecting a different set and acting again proceeds normally. Minting it inside the
   * mutation would give every click a fresh key and protect nothing.
   */
  const batchRef = React.useMemo(() => randomUUID(), [selected.join(',')]);

  const runBulk = async (action: 'mark_paid' | 'mark_unpaid') => {
    const result = await bulkAction.mutateAsync({ policyIds: selected, action, clientRef: batchRef });
    setSelected([]);

    if (result.failed.length) {
      toast.warning(`${result.succeeded.length} ✓ · ${result.failed.length} ✕`, {
        description: result.failed[0]?.message,
      });
    } else {
      toast.success(tn('count.updated', result.succeeded.length));
    }
  };

  /*
   * Declared once, rendered in two places: each filter carrying a `column` lands in the
   * table's filter row beneath that column, and the rest — the plate lookup and the
   * non-renewing switch, which narrow the whole book rather than one column — stay in the
   * toolbar. The narrow-screen popover still holds all of them.
   */
  const filters = React.useMemo<FilterDescriptor[]>(
    () => [
      {
        key: 'insuranceCompanyId',
        type: 'options',
        label: t('policy.company'),
        column: 'insuranceCompanyName',
        options: (insurers ?? []).map((insurer) => ({ value: insurer.id, label: insurer.name })),
      },
      { key: 'insuranceType', type: 'enum', enum: 'insuranceType', label: t('policy.insuranceType'), multiple: true, column: 'insuranceType' },
      { key: 'status', type: 'enum', enum: 'policyStatus', label: t('policy.status'), multiple: true, column: 'status' },
      { key: 'paymentStatus', type: 'enum', enum: 'paymentStatus', label: t('policy.paymentStatus'), multiple: true, column: 'paymentStatus' },
      { key: 'expirationMonth', type: 'month', label: t('policy.endDate'), column: 'endDate' },
      { key: 'plateNumber', type: 'text', label: t('policy.plate'), placeholder: t('policy.plate') },
      { key: 'showNonRenewing', type: 'switch', label: t('policy.showNonRenewing') },
      /* The worklist the commission screen links to, and the way back out of it. */
      { key: 'missingNetPremium', type: 'switch', label: t('policy.netPremiumMissing') },
    ],
    [t, insurers],
  );

  return (
    <>
      <PageHeader
        title={t('policy.title')}
        description={t('policy.subtitle')}
        actions={
          can('policy:write') && (
            <Button onClick={() => setFormOpen(true)}>
              <Plus /> {t('action.newPolicy')}
            </Button>
          )
        }
      />

      <FilterBar
        className="mb-5"
        listState={list}
        inline="unbound"
        searchPlaceholder={`${t('policy.number')} / ${t('client.one')}`}
        filters={filters}
        actions={
          can('report:export') && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => download('/dashboard/export', 'policies.xlsx', list.filters)}
            >
              <Download /> {t('action.export')}
            </Button>
          )
        }
      />

      <DataTable
        columns={columns}
        rows={data?.data}
        total={data?.meta.total}
        listState={list}
        filters={filters}
        isLoading={isLoading}
        isFetching={isFetching}
        error={error}
        onRetry={refetch}
        getRowId={(policy) => policy.id}
        onRowClick={(policy) => navigate(`/policies/${policy.id}`)}
        rowClassName={policyRowTint}
        emptyTitle={t('common.noResults')}
        selection={
          can('policy:renew')
            ? {
                selectedIds: selected,
                onChange: setSelected,
                actions: (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      loading={bulkAction.isPending}
                      onClick={() => void runBulk('mark_paid')}
                    >
                      <Wallet /> {t('action.markPaid')}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      loading={bulkAction.isPending}
                      onClick={() => void runBulk('mark_unpaid')}
                    >
                      {t('enum.paymentStatus.unpaid')}
                    </Button>
                  </>
                ),
              }
            : undefined
        }
        footer={
          totals && (
            <>
              <span className="text-muted-foreground">
                {t('policy.premium')}{' '}
                <span className={cn('text-foreground font-semibold tabular')}>{money(totals.premium)}</span>
              </span>
              <span className="text-muted-foreground">
                {t('policy.commission')}{' '}
                <span className="text-foreground font-semibold tabular">{money(totals.commission)}</span>
              </span>
            </>
          )
        }
      />

      {payingPolicy && (
        <MarkPaidDialog
          policy={payingPolicy}
          open
          onOpenChange={(open) => !open && setPayingPolicy(null)}
        />
      )}

      <PolicyFormDialog open={formOpen} onOpenChange={setFormOpen} />
    </>
  );
}
